import React, { useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { useNotificationStore } from '../../stores/useNotificationStore';
import apiClient from '../../utils/apiClient';
import { StudentNotification } from '../../types';
import BellIcon from '../icons/BellIcon';
import { useLabels } from '../../hooks/useLabels';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PaginatedNotificationResponse {
  data: StudentNotification[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface NotificationCenterProps {
  /** Controlled open state managed by the parent Header */
  isOpen: boolean;
  /** Callback to toggle this dropdown (parent handles mutual exclusion) */
  onToggle: () => void;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onToggle }) => {
  const { getLabel } = useLabels();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { notifications, unreadCount, setNotifications, markAsRead, markAllAsRead } =
    useNotificationStore();

  // ── Fetch notifications on mount (auth-guarded) ─────────────────────────────
  useEffect(() => {
    if (isAuthLoading || !user) return;

    const fetchNotifications = async () => {
      try {
        const response = await apiClient.get<PaginatedNotificationResponse>('/notifications', {
          params: { page: 1, pageSize: 10, orderBy: 'dateCreated:desc', status: 'UNREAD' },
        });
        const data = Array.isArray(response) ? response : response?.data ?? [];
        setNotifications(data);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
  }, [setNotifications, user, isAuthLoading]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'PAYMENT':  return <FiCheckCircle className="h-4 w-4 text-success" />;
      case 'SECURITY': return <FiXCircle className="h-4 w-4 text-danger" />;
      default:         return <BellIcon className="h-4 w-4 text-theme-primary" />;
    }
  };

  return (
    <div className="relative">
      {/* ── Bell Trigger ──────────────────────────────────────────────────── */}
      <button
        id="notification-center-button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
        className={`relative inline-flex items-center justify-center p-2 rounded-full shadow-sm ring-1 ring-inset transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-theme-primary
          ${isOpen
            ? 'text-theme-primary bg-theme-primary/10 ring-theme-primary/40'
            : 'text-theme-sidebar-icon ring-theme-divider hover:bg-theme-primary/10 hover:text-theme-primary hover:ring-theme-primary/40'
          }`}
        title={getLabel('header_notifications_title')}
        aria-label={getLabel('header_notifications_title')}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-danger text-white text-[10px] font-bold ring-2 ring-offset-1 ring-offset-transparent">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Notification Panel ────────────────────────────────────────────── */}
      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition ease-out duration-300"
        enterFrom="transform opacity-0 -translate-y-2 scale-95"
        enterTo="transform opacity-100 translate-y-0 scale-100"
        leave="transition ease-in duration-200"
        leaveFrom="transform opacity-100 translate-y-0 scale-100"
        leaveTo="transform opacity-0 -translate-y-2 scale-95"
      >
        <div className="absolute right-0 mt-3 w-80 bg-[rgb(var(--color-stats-container-bg-rgb))]/95 backdrop-blur-md rounded-xl shadow-2xl z-50 overflow-hidden border border-theme-divider origin-top-right">

          {/* Panel header */}
          <div className="px-4 py-3 border-b border-theme-divider flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-theme-primary animate-pulse" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-theme-text-primary">
                {getLabel('header_notifications_title')}
              </h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-theme-primary/10 text-theme-primary">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs font-semibold text-theme-primary hover:opacity-70 transition-opacity"
              >
                {getLabel('header_mark_all_read')}
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <BellIcon className="h-8 w-8 text-theme-text-secondary opacity-30 mb-2" />
                <p className="text-sm text-theme-text-secondary opacity-70">
                  {getLabel('header_notifications_empty')}
                </p>
              </div>
            ) : (
              <ul>
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={`border-b border-theme-divider last:border-b-0 transition-colors duration-200 ${
                      notification.status === 'UNREAD'
                        ? 'bg-theme-primary/5 hover:bg-theme-primary/10'
                        : 'hover:bg-theme-primary/5'
                    }`}
                  >
                    <div className="flex items-start gap-3 p-4">
                      {/* Icon */}
                      <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center ${
                        notification.status === 'UNREAD' ? 'bg-theme-primary/10' : 'bg-theme-divider/50'
                      }`}>
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-grow min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          notification.status === 'UNREAD' ? 'text-theme-text-primary' : 'text-theme-text-secondary'
                        }`}>
                          {notification.subject}
                        </p>
                        <p className="text-xs text-theme-text-secondary opacity-75 mt-0.5 line-clamp-2">
                          {notification.messageSnippet}
                        </p>
                        <p className="text-[10px] text-theme-text-secondary opacity-50 mt-1 font-mono">
                          {formatDistanceToNow(new Date(notification.date), { addSuffix: true })}
                        </p>
                      </div>

                      {/* Mark as read */}
                      {notification.status === 'UNREAD' && (
                        <button
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="flex-shrink-0 text-theme-primary hover:opacity-70 transition-opacity mt-0.5"
                          title={getLabel('header_mark_as_read')}
                        >
                          <FiCheckCircle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default NotificationCenter;
